import React from 'react';
import { modals, Stack, Text } from '@cad-arc';
import { EventDetailsTabEventDto, EventResource, BoloAssociation } from '@mark43/cad-api';
import { TabSection } from '../../../../../panels/entity-panels/tabs/tab-section';
import { EventList } from '../helpers/event-list';
import { useApiClient } from '@m43/core';
import { useEnabledFeatures } from '../../../../../common/hooks/enabled-features/use-enabled-features';
import { useMutation } from '@tanstack/react-query';
import { useWorkspacePanel } from '../../../../../panels/hooks/use-workspace-panel';
import { EntityPanelBoloAssociations } from '../../../../../panels/entity-panels/related-data/entity-panel-bolo-associations';

export function RelatedEventsSection({
    crossClassEvents,
    copiedEvents,
    associatedEvents,
    bolos,
}: {
    crossClassEvents: EventDetailsTabEventDto[];
    copiedEvents: EventDetailsTabEventDto[];
    associatedEvents: EventDetailsTabEventDto[];
    bolos: BoloAssociation[];
}): React.ReactElement {
    const eventId = useWorkspacePanel().linkedEntityId;
    const apiClient = useApiClient();

    const unassociateEvent = useMutation((params: { eventIds: string[] }) => {
        return apiClient.request<EventResource.UnassociateEvents>({
            method: 'POST',
            url: `event/unassociate/${eventId}/`,
            data: {
                unassociateAgencyEventNumbers: params.eventIds,
            },
        });
    });

    const { unassociateEvents, viewBolos } = useEnabledFeatures();

    const handleDeleteAssociatedEvent = unassociateEvents
        ? (params: Pick<EventDetailsTabEventDto, 'eventId' | 'eventNumber'>): Promise<unknown> => {
              return modals.confirm({
                  onConfirm: () => {
                      return unassociateEvent.mutateAsync({
                          eventIds: [String(params.eventNumber)],
                      });
                  },
                  body: `Remove associated event #${params.eventNumber}?`,
                  title: 'Remove Associated Event',
              });
          }
        : undefined;

    return (
        <TabSection title="Related Events">
            <Stack spacing={3}>
                <EventList
                    events={crossClassEvents}
                    title="X-Class"
                    renderLabel={(event) => {
                        return [event.eventClass, 'Event #'].filter(Boolean).join(' ');
                    }}
                />
                <EventList
                    events={copiedEvents}
                    title="Copied Events"
                    renderLabel={(event) => {
                        return event.copiedFrom ? 'Copied From #' : 'Copied To #';
                    }}
                />
                <EventList
                    events={associatedEvents}
                    onDelete={handleDeleteAssociatedEvent}
                    renderLabel={() => 'Event #'}
                    title="Associated Events"
                />
                {viewBolos && (
                    <>
                        <Text color="tertiary">Associated BOLOs</Text>
                        <EntityPanelBoloAssociations panelType="EVENT" associations={bolos} />
                    </>
                )}
            </Stack>
        </TabSection>
    );
}
